import {useQuery} from '@tanstack/react-query'
import {Download, Loader2} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {formatTimeOnly} from '@/lib/date-utils'
import {LogLevel} from '@/types/background-jobs'
import type {ExecutionLog} from '@/types/background-jobs'

interface ExecutionLogViewerProps {
    referenceId: string | null
    referenceName: string
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function ExecutionLogViewer({
    referenceId,
    referenceName,
    open,
    onOpenChange,
}: ExecutionLogViewerProps) {
    const services = useServices()
    const i18n = useI18n()
    const {data: logsPage, isLoading} = useQuery({
        queryKey: ['execution-logs', referenceId],
        queryFn: () => services.executionLogs.byReference(referenceId!, {size: 500}),
        enabled: !!referenceId && open,
    })

    const logs = (logsPage?.content ?? []) as ExecutionLog[]

    const getLogLevelBadge = (level: string) => {
        switch (level) {
            case LogLevel.ERROR:
                // eslint-disable-next-line i18next/no-literal-string
                return <Badge variant="destructive" className="text-xs">ERROR</Badge>
            case LogLevel.WARN:
                // eslint-disable-next-line i18next/no-literal-string
                return <Badge variant="default" className="bg-status-warning text-xs">WARN</Badge>
            case LogLevel.INFO:
                // eslint-disable-next-line i18next/no-literal-string
                return <Badge variant="default" className="bg-status-info text-xs">INFO</Badge>
            case LogLevel.DEBUG:
                // eslint-disable-next-line i18next/no-literal-string
                return <Badge variant="outline" className="text-xs">DEBUG</Badge>
            default:
                return <Badge variant="outline" className="text-xs">{level}</Badge>
        }
    }
    void getLogLevelBadge

    const handleDownload = () => {
        const logText = logs.map(log =>
            `${log.createdAt} [${log.logLevel}] ${log.message}`
        ).join('\n')
        // eslint-disable-next-line i18next/no-literal-string
        const blob = new Blob([logText], {type: 'text/plain'})
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${referenceName.replace(/[^a-zA-Z0-9]/g, '_')}_logs.txt`
        a.click()
        URL.revokeObjectURL(url)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[80vh]">
                <DialogHeader className="pr-8">
                    <DialogTitle>
                        {i18n.executionLog.executionLogs({name: referenceName})}
                    </DialogTitle>
                </DialogHeader>
                <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                        {isLoading ? i18n.common.loading : i18n.executionLog.logEntries({count: String(logs.length)})}
                    </span>
                    {logs.length > 0 && (
                        <Button variant="outline" size="sm" onClick={handleDownload} data-testid="btn-download-logs">
                            <Download className="h-4 w-4 mr-2"/>
                            {i18n.executionLog.download}
                        </Button>
                    )}
                </div>
                <div className="h-[60vh] overflow-y-auto">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground"/>
                        </div>
                    ) : logs.length > 0 ? (
                        <div className="font-mono text-sm space-y-1">
                            {logs.map((log) => (
                                <div
                                    key={log.id}
                                    className={`py-1 px-2 rounded ${
                                        log.logLevel === LogLevel.ERROR ? 'bg-status-danger-subtle text-status-danger dark:bg-status-danger-subtle dark:text-status-danger-muted' :
                                            log.logLevel === LogLevel.WARN ? 'bg-status-warning-subtle text-status-warning dark:bg-status-warning-subtle dark:text-status-warning-muted' :
                                                log.logLevel === LogLevel.DEBUG ? 'text-muted-foreground' :
                                                    ''
                                    }`}
                                >
                                    <span className="text-muted-foreground text-xs">
                                        {formatTimeOnly(log.createdAt)}
                                    </span>
                                    {' '}
                                    <span className={`font-semibold ${
                                        log.logLevel === LogLevel.ERROR ? 'text-status-danger' :
                                            log.logLevel === LogLevel.WARN ? 'text-status-warning' :
                                                log.logLevel === LogLevel.DEBUG ? 'text-gray-400' :
                                                    'text-status-info'
                                    }`}>
                                        [{log.logLevel}]
                                    </span>
                                    {' '}
                                    {log.message}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-center text-muted-foreground py-8">{i18n.executionLog.noLogs}</p>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
